import { PathFinder, AlgorithmOptions } from '../core/PathFinder';
import { PathResult, AlgorithmStep } from '../core/PathResult';
import { BaseGrid } from '@/grid/BaseGrid';
import { Cell, CellState } from '@/grid/Cell';

export class BFS extends PathFinder {
  readonly name = 'Breadth-First Search';
  readonly description = 'Explores all nodes at current depth before moving deeper';

  findPath(grid: BaseGrid, start: Cell, goal: Cell, options?: AlgorithmOptions): PathResult {
    const startTime = performance.now();
    const steps: AlgorithmStep[] = [];

    const generator = this.findPathStepByStep(grid, start, goal, options);

    let result = generator.next();
    while (!result.done) {
      steps.push(result.value);
      result = generator.next();
    }

    const executionTime = performance.now() - startTime;
    const pathResult = result.value;

    return new PathResult(
      pathResult.path,
      pathResult.nodesExplored,
      executionTime,
      pathResult.success,
      steps
    );
  }

  *findPathStepByStep(
    grid: BaseGrid,
    start: Cell,
    goal: Cell,
    _options?: AlgorithmOptions
  ): Generator<AlgorithmStep, PathResult, undefined> {
    const startTime = performance.now();

    grid.reset();

    const queue: Cell[] = [];
    const visited = new Set<Cell>();
    let closedCount = 0;

    start.parent = null;
    queue.push(start);
    visited.add(start);
    start.state = CellState.OPEN;

    yield {
      type: 'open',
      cell: start,
      cost: 0,
      openListSize: queue.length,
      closedListSize: closedCount
    };

    let nodesExplored = 0;

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) break;

      if (current.equals(goal)) {
        const path = this.reconstructPath(goal);
        const executionTime = performance.now() - startTime;

        yield {
          type: 'path_found',
          cell: current,
          openListSize: queue.length,
          closedListSize: closedCount
        };

        return new PathResult(path, nodesExplored, executionTime, true);
      }

      current.state = CellState.CLOSED;
      closedCount++;
      nodesExplored++;

      yield {
        type: 'close',
        cell: current,
        openListSize: queue.length,
        closedListSize: closedCount
      };

      const neighbors = grid.getNeighbors(current);

      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          neighbor.parent = current;
          queue.push(neighbor);
          visited.add(neighbor);
          neighbor.state = CellState.OPEN;

          yield {
            type: 'open',
            cell: neighbor,
            parent: current,
            openListSize: queue.length,
            closedListSize: closedCount
          };
        }
      }
    }

    const executionTime = performance.now() - startTime;
    return PathResult.failure(nodesExplored, executionTime);
  }
}
